import { useState, useCallback } from "react";
import {
  BulkMessageSendStatusEnum,
  BulkMessageSortableFieldEnum,
  SortDirectionEnum,
  BulkMessageCampaignTypeEnum,
  User,
} from "@/__generated__/types";
import { useBulkMessages } from "@/hooks/useBulkMessages";
import { useOrg } from "@/hooks/useOrg";
import { useCounselors } from "@/hooks/useCounselors";
import { useTranslation } from "react-i18next";
import { CustomFilterTable } from "@/components/DataTable/Header/Filter";
import {
  DEFAULT_INITIAL_RECORDS_ACTIVE_SCHEDULED,
  getPageCount,
} from "./utils";

const BulkMessageColumnsId = {
  name: "name",
  campaignType: "campaignType",
  createdBy: "createdBy",
  startSendingAt: "startSendingAt",
  actions: "actions",
} as const;

const filtersMapI18nKey: Record<BulkMessageCampaignTypeEnum, string> = {
  [BulkMessageCampaignTypeEnum.OneTimeSend]:
    "bulkMessaging.filters.oneTimeSend",
  [BulkMessageCampaignTypeEnum.Campaign]: "bulkMessaging.filters.campaign",
};

export const useBulkMessagesTable = () => {
  const { t } = useTranslation();
  const { org } = useOrg();
  const { data: counselors } = useCounselors();
  const [pageIndex, setPageIndex] = useState(0);
  const [after, setAfter] = useState<string | null>(null);
  const [before, setBefore] = useState<string | null>(null);
  const [selectedFilterValues, setSelectedFilterValues] = useState<
    Record<string, string | undefined>
  >({});
  const [selectedFilters, setSelectedFilters] = useState<{
    createdBy?: string[];
    campaignType?: BulkMessageCampaignTypeEnum[];
  }>({});

  const bulkMessagesParams = {
    orgId: org?.id,
    sendStatus: [
      BulkMessageSendStatusEnum.Active,
      BulkMessageSendStatusEnum.Scheduled,
    ],
    campaignType: selectedFilters.campaignType || [],
    first:
      after || (!after && !before)
        ? DEFAULT_INITIAL_RECORDS_ACTIVE_SCHEDULED
        : undefined,
    last: before ? DEFAULT_INITIAL_RECORDS_ACTIVE_SCHEDULED : undefined,
    createdBy: selectedFilters.createdBy,
    ordering: [
      {
        field: BulkMessageSortableFieldEnum.StartSendingAt,
        direction: SortDirectionEnum.Desc,
      },
    ],
    after,
    before,
  };

  const {
    messagesData,
    loading,
    hasNextPage,
    hasPreviousPage,
    endCursor,
    startCursor,
    totalCount,
  } = useBulkMessages(bulkMessagesParams);

  const handleFilterApply = useCallback(
    (filterId: string, values: string[]): void => {
      setPageIndex(0);
      setAfter(null);
      setBefore(null);
      setSelectedFilters((prev) => {
        const newFilters = { ...prev };
        const [type] = values;

        switch (filterId) {
          case BulkMessageColumnsId.createdBy:
            if (values.length === 0) {
              setSelectedFilterValues((prev) => ({
                ...prev,
                [filterId]: undefined,
              }));
              newFilters.createdBy = undefined;
            } else {
              setSelectedFilterValues((prev) => ({
                ...prev,
                [filterId]: values[0],
              }));
              newFilters.createdBy = values;
            }
            break;

          case BulkMessageColumnsId.campaignType:
            if (values.length === 0) {
              setSelectedFilterValues((prev) => ({
                ...prev,
                [filterId]: undefined,
              }));
              newFilters.campaignType = undefined;
            } else {
              setSelectedFilterValues((prev) => ({
                ...prev,
                [filterId]: type,
              }));
              if (type === t("bulkMessaging.filters.campaign")) {
                newFilters.campaignType = [
                  BulkMessageCampaignTypeEnum.Campaign,
                ];
              } else if (type === t("bulkMessaging.filters.oneTimeSend")) {
                newFilters.campaignType = [
                  BulkMessageCampaignTypeEnum.OneTimeSend,
                ];
              }
            }
            break;
        }

        return newFilters;
      });
    },
    [t],
  );

  const getFilterDisplayValue = useCallback((): string | undefined => {
    const createdByValue = selectedFilterValues[BulkMessageColumnsId.createdBy];
    const campaignTypeValue =
      selectedFilterValues[BulkMessageColumnsId.campaignType];

    if (createdByValue && createdByValue !== undefined) {
      const counselor = counselors?.find((c) => c?.id === createdByValue);
      if (counselor) {
        return `${counselor.firstName} ${counselor.lastName}`;
      }
      return createdByValue;
    }

    if (campaignTypeValue && campaignTypeValue !== undefined) {
      return campaignTypeValue;
    }

    return undefined;
  }, [selectedFilterValues, counselors]);

  const handleNextPage = useCallback(() => {
    setBefore(null);
    setAfter(endCursor || null);
    setPageIndex(pageIndex + 1);
  }, [endCursor, pageIndex]);

  const handlePreviousPage = useCallback(() => {
    setAfter(null);
    setBefore(startCursor || null);
    setPageIndex(pageIndex - 1);
  }, [startCursor, pageIndex]);

  const createCreatedByFilter = (): CustomFilterTable => ({
    id: BulkMessageColumnsId.createdBy,
    label: t("bulkMessaging.communityToolsOverview.columns.createdBy"),
    values:
      counselors
        ?.filter((counselor): counselor is User => counselor !== null)
        .map((counselor: User) => ({
          value: counselor.id,
          user: counselor,
        })) ?? [],
    avatarFilterLabel: true,
  });

  const createCampaignTypeFilter = (): CustomFilterTable => ({
    id: BulkMessageColumnsId.campaignType,
    label: t("bulkMessaging.table.campaignType"),
    values: [
      BulkMessageCampaignTypeEnum.Campaign,
      BulkMessageCampaignTypeEnum.OneTimeSend,
    ].map((type) => ({
      value: t(filtersMapI18nKey[type]),
    })),
    avatarFilterLabel: false,
  });

  const customFilters: CustomFilterTable[] = [
    createCreatedByFilter(),
    createCampaignTypeFilter(),
  ];

  return {
    messagesData,
    loading,
    hasNextPage,
    hasPreviousPage,
    pageIndex,
    totalCount,
    pageCount: getPageCount(totalCount),
    handleFilterApply,
    getFilterDisplayValue,
    handleNextPage,
    handlePreviousPage,
    customFilters,
  };
};
